import { Link, useNavigate } from 'react-router-dom'
import type { AppEntry } from '../store/appsSlice'
import './AppCard.css'

interface AppCardProps {
  app: AppEntry
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffSec = Math.floor(diffMs / 1000)
  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDays = Math.floor(diffHr / 24)
  return `${diffDays}d ago`
}

function AppCard({ app }: AppCardProps) {
  const navigate = useNavigate()
  const routePath = `/apps/${app.id}`

  return (
    <Link
      to={routePath}
      className="app-card"
      data-testid="app-card"
      aria-label={`${app.name} — ${app.description}`}
    >
      <div className="app-card__top">
        <h3 className="app-card__title" data-testid="app-card-title">{app.name}</h3>
        <p className="app-card__description" data-testid="app-card-description">{app.description}</p>
      </div>

      <div className="app-card__bottom">
        <div className="app-card__meta" data-testid="app-card-last-updated">
          <span className="app-card__updated">
            {app.last_updated
              ? `Updated ${formatRelativeTime(app.last_updated)}`
              : `Created ${formatRelativeTime(app.created_at)}`}
          </span>
          <span className="app-card__separator">·</span>
          <span className="app-card__route" data-testid="app-card-route">{routePath}</span>
          <button
            className="app-card__view-btn"
            data-testid="app-card-view-btn"
            onClick={(e) => {
              e.stopPropagation()
              navigate(routePath)
            }}
          >
            View App
          </button>
        </div>
        <div className="app-card__timestamps" data-testid="app-card-timestamps">
          <span className="app-card__timestamp" data-testid="app-card-queued-at">
            Queued {formatRelativeTime(app.created_at)}
          </span>
          {app.started_at && (
            <span className="app-card__timestamp" data-testid="app-card-started-at">
              Started {formatRelativeTime(app.started_at)}
            </span>
          )}
          {app.finished_at && (
            <span className="app-card__timestamp" data-testid="app-card-finished-at">
              Finished {formatRelativeTime(app.finished_at)}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

export default AppCard
